import type {
    ListMessageCommentsFilters,
    MessageComment,
} from 'src/domain/message';
import type { PaginatedResponse } from 'src/domain/pagination';

export const COMMENT_PREVIEW_LIMIT = 3;
const COMMENTS_PAGE_SIZE = 50;

type ListComments = (
    messageId: string,
    filters: ListMessageCommentsFilters,
) => Promise<PaginatedResponse<MessageComment> | MessageComment[]>;

export async function loadMessageComments(
    listComments: ListComments,
    messageId: string,
    expanded: boolean,
): Promise<PaginatedResponse<MessageComment>> {
    const limit = expanded ? COMMENTS_PAGE_SIZE : COMMENT_PREVIEW_LIMIT;
    const firstPage = await listComments(messageId, {
        limit,
        pageIndex: 1,
        sortOrder: 'DESC',
    });
    if (Array.isArray(firstPage)) {
        return { data: firstPage, info: { total: firstPage.length } };
    }

    const comments = [...firstPage.data];
    const total = firstPage.info?.total ?? comments.length;
    const pageSize = firstPage.info?.pageSize ?? limit;
    if (expanded && pageSize > 0) {
        const pageCount = Math.ceil(total / pageSize);
        for (let pageIndex = 2; pageIndex <= pageCount; pageIndex += 1) {
            const page = await listComments(messageId, {
                limit: pageSize,
                pageIndex,
                sortOrder: 'DESC',
            });
            const data = Array.isArray(page) ? page : page.data;
            comments.push(...data);
            if (!data.length) break;
        }
    }

    return {
        data: Array.from(new Map(comments.map(c => [c.id, c])).values()),
        info: { ...firstPage.info, total },
    };
}
